import { useEffect, useRef, useState } from "react";
import { EventBus } from "../game/EventBus";

// Site-wide toast stack (macOS / Win11 style): bubbles slide in at the top
// right, hold 5s, fade out; click skips. Mounted ONCE in App.tsx so it works
// on every screen (login/account errors included — no more alert() popups).
// The stack hugs the top-right and FLOATS OVER the countdown/? widgets
// (z 2500) rather than dodging them — toasts are transient, widgets persist.
//
// Usage from anywhere:  toast("Title", "message")  or
//                       toast("Title", "message", "error")

const SHOW_TOAST = "ro-show-toast";

export type ToastVariant = "info" | "error";

export interface ToastMessage {
  title: string;
  message: string;
  variant?: ToastVariant;
}

export const toast = (
  title: string,
  message: string,
  variant: ToastVariant = "info",
) => {
  EventBus.emit(SHOW_TOAST, { title, message, variant });
};

interface ToastEntry extends ToastMessage {
  id: number;
  closing: boolean;
}

const TOAST_HOLD_MS = 5000;
const TOAST_FADE_MS = 450;

let nextToastId = 1;

export function Toasts() {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timersRef = useRef<Record<number, ReturnType<typeof setTimeout>[]>>({});

  useEffect(() => {
    const beginClose = (id: number) => {
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, closing: true } : t)),
      );
      const removeTimer = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        delete timersRef.current[id];
      }, TOAST_FADE_MS);
      (timersRef.current[id] ||= []).push(removeTimer);
    };
    const show = (data: ToastMessage) => {
      const id = nextToastId++;
      setToasts((prev) => [...prev, { ...data, id, closing: false }]);
      const closeTimer = setTimeout(() => beginClose(id), TOAST_HOLD_MS);
      timersRef.current[id] = [closeTimer];
    };

    EventBus.on(SHOW_TOAST, show);
    const timers = timersRef.current;
    return () => {
      EventBus.removeListener(SHOW_TOAST, show);
      Object.values(timers).forEach((list) => list.forEach(clearTimeout));
    };
  }, []);

  const dismiss = (id: number) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, closing: true } : t)),
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_FADE_MS);
  };

  return (
    <div className="ro-toast-stack">
      <style>{TOAST_CSS}</style>
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`ro-toast ${t.variant === "error" ? "ro-toast-error" : ""} ${
            t.closing ? "ro-toast-out" : ""
          }`}
          onClick={() => dismiss(t.id)}
        >
          <div className="ro-toast-title">{t.title}</div>
          <div className="ro-toast-body">{t.message}</div>
        </div>
      ))}
    </div>
  );
}

const TOAST_CSS = `
.ro-toast-stack{position:fixed;top:30px;right:12px;z-index:2500;
  display:flex;flex-direction:column;gap:10px;align-items:flex-end;
  pointer-events:none;}
.ro-toast{pointer-events:auto;cursor:pointer;width:300px;
  background:rgba(8,14,20,0.92);backdrop-filter:blur(6px);
  border:1px solid var(--ro-accent);border-radius:10px;
  padding:12px 14px;color:var(--ro-text);
  font-family:'MelGame','Courier New',monospace;
  box-shadow:0 8px 24px rgba(0,0,0,0.55),0 0 12px rgba(31,168,224,0.25);
  animation:ro-toast-in 0.28s cubic-bezier(0.21,1.02,0.73,1) both;
  transition:opacity ${TOAST_FADE_MS}ms ease, transform ${TOAST_FADE_MS}ms ease;}
.ro-toast-out{opacity:0;transform:translateY(-8px);}
@keyframes ro-toast-in{
  from{opacity:0;transform:translateX(40px);}
  to{opacity:1;transform:translateX(0);}}
.ro-toast-title{font-size:15px;font-weight:700;color:var(--ro-accent-bright);
  margin-bottom:4px;letter-spacing:0.04em;}
.ro-toast-body{font-size:14px;line-height:1.45;color:var(--ro-text);}
.ro-toast-error{border-color:#ff4444;
  box-shadow:0 8px 24px rgba(0,0,0,0.55),0 0 12px rgba(255,60,60,0.3);}
.ro-toast-error .ro-toast-title{color:#ff6b6b;}
`;

export default Toasts;
